import PropTypes from 'prop-types';
import { useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Grid,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  LinearProgress
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MainCard from 'ui-component/cards/MainCard';
import SkeletonTotalCard from 'ui-component/cards/Skeleton/TotalIncomeCard';

// ==============================|| STYLED COMPONENTS ||============================== //

const CardWrapper = styled(MainCard)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
  color: '#fff',
  overflow: 'hidden',
  position: 'relative',
  border: 'none',
  borderRadius: '12px',
  boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  minHeight: '160px',
  maxHeight: '160px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px 0 rgba(0, 0, 0, 0.15)'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 90% 10%, ${theme.palette.secondary.light}30 0%, transparent 50%)`,
    pointerEvents: 'none'
  },
  '& > div': {
    position: 'relative',
    zIndex: 5
  }
}));

const MetricItem = ({ label, value, subValue, trend }) => (
  <Stack spacing={0.5}>
    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.7rem' }}>
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
      {trend && (
        <Typography variant="caption" sx={{ color: trend > 0 ? '#69f0ae' : '#ff5252' }}>
          {trend > 0 ? '+' : ''}
          {trend}%
        </Typography>
      )}
    </Stack>
  </Stack>
);

const BotonInfo = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 4,
  right: 4,
  color: 'rgba(255, 255, 255, 0.7)',
  padding: 2,
  '&:hover': {
    color: 'rgba(255, 255, 255, 1)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)'
  }
}));

// ==============================|| COMPONENT ||============================== //

const FinancialMetrics = ({ isLoading, metricsData }) => {
  const theme = useTheme();
  const [showPlans, setShowPlans] = useState(false);

  if (isLoading) {
    return <SkeletonTotalCard />;
  }

  const { mrr, arr, arpu, churnRate, planBreakdown } = metricsData || {
    mrr: 0,
    arr: 0,
    arpu: 0,
    churnRate: 0,
    planBreakdown: []
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <CardWrapper border={false} content={false}>
      <Box sx={{ p: 2, position: 'relative', height: '100%' }}>
        {/* Toggle View Button */}
        <Tooltip title={showPlans ? 'Ver Métricas Generales' : 'Ver Detalles de Planes'}>
          <BotonInfo size="small" onClick={() => setShowPlans(!showPlans)}>
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          </BotonInfo>
        </Tooltip>

        {!showPlans ? (
          // MAIN METRICS VIEW
          <Grid container spacing={1} height="100%">
            {/* Sección Principal */}
            <Grid item xs={12}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box flex={1}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontWeight: 400,
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      mb: 0.5
                    }}
                  >
                    MRR (Mensual)
                  </Typography>

                  <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        fontSize: '2rem',
                        lineHeight: 1
                      }}
                    >
                      {formatCurrency(mrr)}
                    </Typography>
                  </Stack>

                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      mt: 0.5,
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    ARR: {formatCurrency(arr)}
                  </Typography>
                </Box>

                {/* Avatar/Icono */}
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  <MonetizationOnIcon sx={{ fontSize: 22 }} />
                </Avatar>
              </Stack>
            </Grid>

            {/* Sección de Estadísticas Adicionales */}
            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                <Stack spacing={0.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '0.7rem'
                      }}
                    >
                      ARPU (Promedio)
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '0.7rem'
                      }}
                    >
                      Tasa de Cancelación
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}
                    >
                      {formatCurrency(arpu)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#ff5252',
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}
                    >
                      {churnRate}%
                    </Typography>
                  </Stack>

                  {/* Visual Divider/Bar for Consistency */}
                  <Box
                    sx={{
                      height: 4,
                      width: '100%',
                      borderRadius: 2,
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      mt: 0.5
                    }}
                  />
                </Stack>
              </Box>
            </Grid>
          </Grid>
        ) : (
          // PLANS BREAKDOWN VIEW
          <Box sx={{ flex: 1, overflow: 'auto', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 2, fontWeight: 600 }}>
              Ingresos por Plan
            </Typography>
            <Stack spacing={1.5}>
              {planBreakdown.length > 0 ? (
                planBreakdown.map((plan, index) => (
                  <Box key={index}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: '#fff' }}>
                        {plan.plan}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(plan.revenue)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <LinearProgress
                        variant="determinate"
                        value={mrr > 0 ? (plan.revenue / mrr) * 100 : 0}
                        sx={{
                          width: '65%',
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.1)',
                          '& .MuiLinearProgress-bar': { bgcolor: '#fff' }
                        }}
                      />
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                        {plan.count} Clientes
                      </Typography>
                    </Stack>
                  </Box>
                ))
              ) : (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  No hay datos de planes
                </Typography>
              )}
            </Stack>
          </Box>
        )}
      </Box>
    </CardWrapper>
  );
};

FinancialMetrics.propTypes = {
  isLoading: PropTypes.bool,
  metricsData: PropTypes.object
};

export default FinancialMetrics;
